import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { CheckCircle2, TrendingUp, Repeat, Clock, AlertTriangle, Lock, Building2 } from 'lucide-react';
import { money, moneyK, fecha, Kpi, Title, Badge, Empty, ESTADO_COBRO, ESTADO_RENOVACION } from './ui';
import SerieFacturacion, { armarSerie } from './SerieFacturacion';

const tooltipStyle = { background: 'var(--tooltip-bg)', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'Raleway', color: 'var(--text-primary)' };

export default function AgenciaPanel() {
  const [d, setD] = useState(null);
  const [consultas, setConsultas] = useState([]);
  const navigate = useNavigate();
  const cargarConsultas = () => api.get('/agency/consultas').then(r => setConsultas(r.data)).catch(() => {});
  useEffect(() => { api.get('/agency/resumen').then(r => setD(r.data)).catch(() => {}); cargarConsultas(); }, []);
  if (!d) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const serie = armarSerie(d.serie);
  const card = { marginBottom: 20 };
  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 8px' };
  const td = { padding: '8px', borderTop: '1px solid var(--border)', fontSize: 13 };

  return (
    <div>
      <Title>Panel de la agencia</Title>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi icono={CheckCircle2} label="Cobrado este mes" value={moneyK(d.mes.total)} sub={`${d.mes.cant} cobros`} />
        <Kpi icono={TrendingUp} label="Cobrado en el año" value={moneyK(d.anio.total)} />
        <Kpi icono={Repeat} label="Abonos mensuales" value={moneyK(d.mrr.total)} sub={`${d.mrr.cant} servicios activos`} color="var(--accent)" />
        <Kpi icono={Clock} label="Por cobrar" value={moneyK(d.pendiente.total)} sub={`${d.pendiente.cant} cobros`} color="#f59e0b" onClick={() => navigate('/agencia/cobros')} />
        <Kpi icono={AlertTriangle} label="Vencido" value={moneyK(d.vencido.total)} sub={`${d.vencido.cant} cobros`} color="#ef4444" onClick={() => navigate('/agencia/cobros')} />
        <Kpi icono={Lock} label="A pasar" value={moneyK((d.a_pasar || {}).total)} sub={`${(d.a_pasar || {}).cant || 0} cuotas futuras`} color="#64748b" onClick={() => navigate('/agencia/cobros')} />
        <Kpi icono={Building2} label="Clientes activos" value={d.clientes_activos} onClick={() => navigate('/agencia/clientes')} />
      </div>

      <div className="card" style={card}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Cobrado y pendiente por mes</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Últimos 12 meses, por categoría. Los pendientes se cuentan en el mes en que había que cobrarlos.</div>
        {serie.length
          ? <SerieFacturacion datos={serie} alto={260} onElegir={() => navigate('/agencia/reportes')} />
          : <Empty>Sin datos</Empty>}
      </div>

      {serie.some(s => s.pendiente > 0) && (
        <div className="card" style={card}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Pendientes por mes</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Cobros de meses anteriores que todavía no entraron</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {serie.filter(s => s.pendiente > 0).map(s => (
              <div key={s.mes} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '8px 12px', minWidth: 120 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{s.mes}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#f59e0b' }}>{moneyK(s.pendiente)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {consultas.filter(c => c.estado === 'nueva').length > 0 && (
        <div className="card" style={{ ...card, borderLeft: '3px solid var(--accent)' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Consultas del portal</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Clientes que pidieron que los contactes</div>
          {consultas.filter(c => c.estado === 'nueva').map(c => (
            <div key={c.id} style={{ borderTop: '1px solid var(--border)', padding: '10px 0', display: 'flex', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, cursor: 'pointer' }} onClick={() => navigate('/agencia/clientes/' + c.cliente_id)}>
                  {c.cliente} · <span style={{ color: 'var(--accent)' }}>{c.servicio || 'un servicio'}</span>
                </div>
                {c.mensaje && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{c.mensaje}</div>}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {c.contacto || c.email} · {fecha(c.created_at)}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={async () => {
                await api.put('/agency/consultas/' + c.id, { estado: 'contactada' }).catch(() => toast.error('Error'));
                cargarConsultas();
              }}>Ya lo contacté</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20 }}>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Por cobrar</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Lo que falta cobrar, por vencimiento</div>
          {d.por_cobrar.length ? (
            <div className="table-container" style={{ border: 0, borderRadius: 0 }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Cliente</th><th style={th}>Vence</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}></th></tr></thead>
              <tbody>{d.por_cobrar.map(c => {
                const e = ESTADO_COBRO[c.estado] || {};
                return (
                  <tr key={c.id}>
                    <td style={td}><div style={{ fontWeight: 600 }}>{c.cliente}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.concepto || '—'}</div></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fecha(c.vencimiento)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(c.monto)}</td>
                    <td style={td}><Badge label={e.label} color={e.color} bg={e.bg} /></td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          ) : <Empty>No hay cobros pendientes 🎉</Empty>}
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Renovaciones de este mes</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Hosting que vence el último día del mes</div>
          {d.renovaciones.length ? (
            <div className="table-container" style={{ border: 0, borderRadius: 0 }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Cliente</th><th style={th}>Vence</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Estado</th></tr></thead>
              <tbody>{d.renovaciones.map(r => {
                const e = ESTADO_RENOVACION[r.estado_renovacion] || {};
                return (
                  <tr key={r.id} onClick={() => navigate('/agencia/renovaciones')} style={{ cursor: 'pointer' }}>
                    <td style={{ ...td, fontWeight: 600 }}>{r.cliente}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fecha(r.proximo_vencimiento)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(r.monto)}</td>
                    <td style={td}><Badge label={e.label} color={e.color} /></td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          ) : <Empty>Este mes no vence ningún hosting</Empty>}
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Últimos pagos</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Lo último que entró</div>
          {d.ultimos_pagos.length ? (
            <div className="table-container" style={{ border: 0, borderRadius: 0 }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>{d.ultimos_pagos.map(p => (
                <tr key={p.id}>
                  <td style={td}><div style={{ fontWeight: 600 }}>{p.cliente}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.concepto || '—'}</div></td>
                  <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{fecha(p.fecha)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: '#22c55e' }}>{money(p.monto)}</td>
                </tr>
              ))}</tbody>
            </table></div>
          ) : <Empty>Sin pagos registrados</Empty>}
        </div>
      </div>
    </div>
  );
}
